/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Idle-Scheduled Autosave Coordinator
   Saves project upon idle without permanent arbitrary interval timers.
   ═══════════════════════════════════════════════════════════════ */

import type { DomainGraphState } from '../domain/types';
import { serializeProject } from '../domain/serialization';
import { saveProjectToDb } from './indexedDb';

export class AutosaveCoordinator {
  private isDirty = false;
  private pendingTimeout: ReturnType<typeof setTimeout> | null = null;
  private idleHandle: number | null = null;
  private onSavedCallback?: () => void;

  constructor(private debounceMs = 2000) {}

  onSaved(cb: () => void): void {
    this.onSavedCallback = cb;
  }

  markDirty(getState: () => { state: DomainGraphState; projectName: string }): void {
    this.isDirty = true;

    // Reset pending debounce timeout
    if (this.pendingTimeout !== null) {
      clearTimeout(this.pendingTimeout);
    }

    this.pendingTimeout = setTimeout(() => {
      this.scheduleIdleSave(getState);
    }, this.debounceMs);
  }

  private scheduleIdleSave(getState: () => { state: DomainGraphState; projectName: string }): void {
    if (!this.isDirty) return;

    const performSave = async () => {
      try {
        const { state, projectName } = getState();
        const serialized = serializeProject(state, projectName);
        await saveProjectToDb(serialized);
        this.isDirty = false;
        this.onSavedCallback?.();
      } catch (err) {
        console.warn('Autosave failed:', err);
      }
    };

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      if (this.idleHandle !== null) {
        cancelIdleCallback(this.idleHandle);
      }
      this.idleHandle = requestIdleCallback(() => {
        performSave();
        this.idleHandle = null;
      }, { timeout: 3000 });
    } else {
      performSave();
    }
  }

  dispose(): void {
    if (this.pendingTimeout !== null) {
      clearTimeout(this.pendingTimeout);
      this.pendingTimeout = null;
    }
    if (this.idleHandle !== null && typeof cancelIdleCallback !== 'undefined') {
      cancelIdleCallback(this.idleHandle);
      this.idleHandle = null;
    }
  }
}
